import { createApi } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import Cookies from "js-cookie";
import getTenantId from "../../components/Tenent";
import { showAccessDeniedModal, showSessionExpiredModal } from "../slices/authSlice";
const getStore = () => require("../store").store;

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_ACTIVITIES_HOST_URL,
  withCredentials: true,
  headers: {
    accept: `application/json`,
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    config.headers["x-tenant-id"] = getTenantId();
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && window.location.pathname !== "/login") {
      getStore().dispatch(showSessionExpiredModal(window.location.pathname));
    }
    return Promise.reject(err);
  },
);

const axiosBaseQuery =
  () =>
  async ({ responseType, url, method, body, params, ...requestOpts }) => {
    try {
      const blobEndpoints = [
        "pcf/generate_pcf_report",
        "cbam/generate_report",
        "cbam/upload_template",
        "/basemetric-spend-download/"
      ];
      const isBlobRequest = blobEndpoints.some((endpoint) =>
        url.includes(endpoint)
      );
      const axiosOptions = {
        url: url,
        method,
        data: body,
        params,
        headers: requestOpts.headers,
        responseType: isBlobRequest ? "blob" : "json",
      };

      const result = await axiosInstance(axiosOptions);
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError;
      const errorData = axiosError.response?.data;
      const status = axiosError.response?.status;

      if (status === 403 && errorData?.error === "forbidden") {
        getStore().dispatch(showAccessDeniedModal());
      }
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const cbam = createApi({
  reducerPath: "cbam",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["cbam", "pcf"],
  endpoints: (builder) => ({
    getProductsForCbam: builder.query({
      query: () => {
        return {
          url: `cbam/get_products`,
        };
      },
      providesTags: ["cbam", "production-data"],
    }),
    getProductsForPcf: builder.query({
      query: () => {
        return {
          url: `pcf/get_products`,
        };
      },
      providesTags: ["pcf"],
    }),
    addProductsForCbam: builder.mutation({
      query: (payload) => {
        return {
          url: `cbam/add_product`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ["cbam"],
    }),
    addProductsForPcf: builder.mutation({
      query: (payload) => {
        return {
          url: `pcf/add_product`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ["pcf"],
    }),
    deleteCbamProducts: builder.mutation({
      query: (ids) => {
        const idParam = ids.join(",");
        return {
          url: `cbam/delete_product?product_id=${idParam}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["cbam"],
    }),
    deleteDisclosureProducts: builder.mutation({
      query: (ids) => {
        const idParam = ids.join(",");
        return {
          url: `cbam/delete_disclosure_product?ids=${idParam}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["cbam"],
    }),
    deletePcfProducts: builder.mutation({
      query: (ids) => {
        const idParam = ids.join(",");
        return {
          url: `pcf/delete_product?product_id=${idParam}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["pcf"],
    }),
    deleteProductActivities: builder.mutation({
      query: ({ productId, activityIds }) => {
        const idsParam = activityIds.join(",");
        return {
          url: `cbam/unset_allocation_data?product_id=${productId}&ids=${idsParam}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["cbam"],
    }),
    deletePcfProductActivities: builder.mutation({
      query: ({ productId, activityIds }) => {
        const idsParam = activityIds.join(",");
        return {
          url: `pcf/unset_allocation_data?product_id=${productId}&activity_ids=${idsParam}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["pcf"],
    }),
    updateCbamProduct: builder.mutation({
      query: (body) => ({
        url: `cbam/update_product`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["cbam"],
    }),
    updatePcfProduct: builder.mutation({
      query: (body) => ({
        url: `pcf/update_product`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["pcf"],
    }),
    getProductAllocationData: builder.query({
      query: (product_id) => ({
        url: `cbam/get_allocation_data`,
        params: { product_id },
      }),
      providesTags: ["cbam"],
    }),
    getPcfProductAllocationData: builder.query({
      query: (product_id) => ({
        url: `pcf/get_allocation_data`,
        params: { product_id },
      }),
      providesTags: ["pcf"],
    }),
    getEmissionSearchResults: builder.query({
      query: ({ searchText, productId, category, lca_stage, scope, node }) => {
        const params = new URLSearchParams({
          search_text: searchText,
          product_id: productId.toString(),
          page: "1",
          limit: "1000",
        });
        if (category) params.append("category", category);
        if (lca_stage) params.append("lca_stage", lca_stage);
        if (scope) params.append("scope", scope);
        if (node) params.append("node", node);

        return {
          url: `/cbam/get_emission_data?${params.toString()}`,
        };
      },
    }),
    getPcfEmissionSearchResults: builder.query({
      query: ({ searchText, productId, category, lca_stage, scope, node }) => {
        const params = new URLSearchParams({
          search_text: searchText,
          product_id: productId.toString(),
          page: "1",
          limit: "20",
        });
        if (category) params.append("category", category);
        if (lca_stage) params.append("lca_stage", lca_stage);
        if (scope) params.append("scope", scope);
        if (node) params.append("node", node);

        return {
          url: `/pcf/get_emission_data?${params.toString()}`,
        };
      },
    }),
    saveAllocationData: builder.mutation({
      query: (payload) => ({
        url: "cbam/set_allocation_data",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["cbam", "supplier-data"],
    }),
    savePcfAllocationData: builder.mutation({
      query: (payload) => ({
        url: "pcf/set_allocation_data",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["pcf", "supplier-data"],
    }),
    getCbamData: builder.query({
      query: () => {
        return {
          url: `cbam/get_cbam_data`,
        };
      },
    }),
    getSupplierData: builder.query({
      query: (product_id) => {
        return {
          url: `cbam/tenant/get_supplier_data`,
          params: { product_id },
        };
      },
      providesTags: ["supplier-data"],
    }),
    setSupplierData: builder.mutation({
      query: (payload) => ({
        url: "/cbam/tenant/set_supplier_data",
        method: "POST",
        body: payload,
      }),
    }),
    sendEmailToSupplier: builder.mutation({
      query: (payload) => ({
        url: "cbam/supplier/send_email",
        method: "POST",
        body: payload,
      }),
    }),
    duplicateCbamProduct: builder.mutation({
      query: (payload) => ({
        url: "cbam/duplicate_product",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["cbam"],
    }),
    duplicatePcfProduct: builder.mutation({
      query: (payload) => ({
        url: "pcf/duplicate_product",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["pcf"],
    }),
    setSupplierDataStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: "cbam/set_data_status",
        method: "POST",
        body: { id, status },
      }),
      invalidatesTags: ["cbam"],
    }),
    getProductPcfReport: builder.query({
      query: (product_id) => ({
        url: `pcf/generate_pcf_report`,
        params: { product_id },
      }),
      responseHandler: (response) => response.blob(),
    }),
    spendSummaryDownload: builder.query({
      query: () => {
        return {
          url: `/basemetric-spend-download/`,
          responseType: "blob",

          responseHandler: (response) => response.blob(),
        };
      },
    }),
    getCbamReport: builder.query({
      query: (product_ids) => ({
        url: `cbam/generate_report`,
        params: { product_ids: product_ids.join(",") },
      }),
      responseHandler: (response) => response.blob(),
    }),
    uploadCbamTemplate: builder.mutation({
      query: (file) => {
        return {
          url: `cbam/upload_products`,
          method: "POST",
          body: file,
        };
      },
      invalidatesTags: ["production-data"],
    }),
    getCbamTemplate: builder.query({
      query: () => ({
        url: `cbam/upload_template`,
      }),
      responseHandler: (response) => response.blob(),
    }),
    getUnits: builder.query({
      query: () => ({
        url: `cbam/units_list`,
      }),
    }),
    getFactors: builder.query({
      query: ({
        search_text = "",
        region = "",
        unit = "",
        page = null,
        limit = null,
      }) => {
        const params = new URLSearchParams();
        if (search_text) params.append("search_text", search_text);
        // if (region) params.append("region", region);
        if (unit) params.append("unit", unit);
        if (page !== null) params.append("page", page);
        if (limit !== null) params.append("limit", limit);

        return {
          url: `/cbam/activity_explorer`,
          params,
        };
      },
    }),
    addActivity: builder.mutation({
      query: (body) => ({
        url: "/cbam/manual_activity",
        method: "POST",
        body,
      }),
      invalidatesTags: ["cbam"],
    }),
    getDisclosureProducts: builder.query({
      query: () => {
        return {
          url: `/cbam/get_disclosure_product`,
        };
      },
      providesTags: ["cbam"],
    }),
    getFilteredProducts: builder.query({
      query: ({ time_period_start, time_period_end, node_id }) => {
        return {
          url: `/cbam/get_filtered_products?time_period_start=${time_period_start}&time_period_end=${time_period_end}&node_id=${node_id}`,
        };
      },
    }),
    addDisclosureProduct: builder.mutation({
      query: (payload) => ({
        url: "/cbam/post_disclosure_product",
        method: "POST",
        body: payload,
      }),
    }),
    validateEmail: builder.query({
      query: (token) => ({
        url: "/cbam/supplier/validate_email",
        params: {token},
      }),
    }),
    getEmissionData: builder.query({
      query: (data) => ({
        url: "/cbam/supplier/get_cbam_data",
        params: {
          supplier: data?.supplier_name,
          id: data?.id
        }
      })
    }),
    getDirectEmissionData: builder.query({
      query: () => ({
        url: "cbam/supplier/get_cbam_factors"
      })
    }),
    saveEmissionDetails: builder.mutation({
      query: (payload) => ({
        url: "cbam/supplier/set_cbam_data",
        method: "POST",
        body: payload,
      }),
    }),
    getProductTypes: builder.query({
      query: () => ({
        url: "cbam/product_type_list",
      }),
    }),
  }),
});

export const {
  useGetProductsForCbamQuery,
  useGetProductsForPcfQuery,
  useAddProductsForCbamMutation,
  useAddProductsForPcfMutation,
  useDeleteCbamProductsMutation,
  useDeletePcfProductsMutation,
  useUpdateCbamProductMutation,
  useUpdatePcfProductMutation,
  useGetProductAllocationDataQuery,
  useGetPcfProductAllocationDataQuery,
  useLazyGetEmissionSearchResultsQuery,
  useLazyGetPcfEmissionSearchResultsQuery,
  useSaveAllocationDataMutation,
  useSavePcfAllocationDataMutation,
  useLazySpendSummaryDownloadQuery,
  useGetCbamDataQuery,
  useDuplicateCbamProductMutation,
  useDuplicatePcfProductMutation,
  useGetSupplierDataQuery,
  useSetSupplierDataMutation,
  useSendEmailToSupplierMutation,
  useDeleteProductActivitiesMutation,
  useDeletePcfProductActivitiesMutation,
  useSetSupplierDataStatusMutation,
  useLazyGetProductPcfReportQuery,
  useLazyGetCbamReportQuery,
  useLazyGetCbamTemplateQuery,
  useUploadCbamTemplateMutation,
  useGetUnitsQuery,
  useGetFactorsQuery,
  useAddActivityMutation,
  useGetDisclosureProductsQuery,
  useDeleteDisclosureProductsMutation,
  useGetFilteredProductsQuery,
  useAddDisclosureProductMutation,
  useValidateEmailQuery,
  useGetEmissionDataQuery,
  useGetDirectEmissionDataQuery,
  useSaveEmissionDetailsMutation,
  useGetProductTypesQuery,
} = cbam;
